import cn from 'classnames';
import {
  type FC,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './ComposerScrollbar.module.scss';

const FADE_MS = 650;
/** The pill's inner corner radius: the thumb's travel starts at the tangent
    of the top corner rounding instead of flying up to the pill's very top
    (the native fluent bar did). */
const TOP_INSET = 23;
const MIN_THUMB_PX = 28;

/**
 * Overlay replacement for the composer textarea's scrollbar.
 *
 * The native bar renders differently per platform (Windows fluent builds
 * draw arrow buttons and sit inset from the pill border), so it is hidden
 * and this thumb renders instead: no arrows, a small fixed gap to the
 * pill's border, travel clamped to the corner tangent, shown while the
 * textarea scrolls, draggable, fading out when scrolling stops.
 */
export const ComposerScrollbar: FC<{
  fieldRef: RefObject<HTMLDivElement | null>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  className?: string;
}> = ({ fieldRef, textareaRef, className }) => {
  const thumbRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);
  const dragRef = useRef<{ startY: number; startScroll: number } | null>(null);

  /** Position/size the thumb; reports whether the textarea can scroll. */
  const update = (): boolean => {
    const ta = textareaRef.current;
    const thumb = thumbRef.current;
    const field = fieldRef.current;
    if (!ta || !thumb || !field) {
      return false;
    }
    const overflow = ta.scrollHeight - ta.clientHeight;
    if (overflow <= 1) {
      setVisible(false);
      return false;
    }
    const track = ta.clientHeight - TOP_INSET;
    const size = Math.max(
      MIN_THUMB_PX,
      track * (ta.clientHeight / ta.scrollHeight),
    );
    const top = TOP_INSET + ((track - size) * ta.scrollTop) / (overflow || 1);
    const taTop = ta.getBoundingClientRect().top;
    const fieldTop = field.getBoundingClientRect().top;
    thumb.style.top = `${taTop - fieldTop + top}px`;
    thumb.style.height = `${size}px`;
    return true;
  };

  /** Show the thumb and arm the fade-out timer. */
  const show = () => {
    clearTimeout(hideTimer.current);
    if (update()) {
      setVisible(true);
    }
    hideTimer.current = window.setTimeout(() => {
      if (!dragRef.current) {
        setVisible(false);
      }
    }, FADE_MS);
  };

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) {
      return;
    }
    const onScroll = () => show();
    // Width/height changes (pane resize rewrap, auto-grow) move the thumb.
    const observer = new ResizeObserver(update);
    observer.observe(ta);
    ta.addEventListener('scroll', onScroll);
    update();
    return () => {
      observer.disconnect();
      ta.removeEventListener('scroll', onScroll);
      clearTimeout(hideTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textareaRef]);

  const onThumbPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const ta = textareaRef.current;
    if (!ta || event.button !== 0) {
      return;
    }
    event.preventDefault();
    dragRef.current = { startY: event.clientY, startScroll: ta.scrollTop };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onThumbPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const ta = textareaRef.current;
    const thumb = thumbRef.current;
    if (!drag || !ta || !thumb) {
      return;
    }
    const overflow = ta.scrollHeight - ta.clientHeight;
    const track = ta.clientHeight - TOP_INSET;
    const size = thumb.offsetHeight;
    const dy = event.clientY - drag.startY;
    ta.scrollTop =
      drag.startScroll + (dy / Math.max(1, track - size)) * overflow;
  };

  const onThumbPointerEnd = () => {
    dragRef.current = null;
  };

  return (
    <div
      ref={thumbRef}
      className={cn(styles.thumb, className)}
      data-visible={visible || undefined}
      data-testid="composer-scrollbar-thumb"
      onPointerDown={onThumbPointerDown}
      onPointerMove={onThumbPointerMove}
      onPointerUp={onThumbPointerEnd}
      onPointerCancel={onThumbPointerEnd}
      aria-hidden="true"
    />
  );
};
